import React, { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api-fetch";
import { authClient } from "../lib/auth-client";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import { OrganizationContext } from "./OrganizationContext.js";
export function OrganizationProvider({ children }) {
  const { data: session, isPending: sessionPending } = authClient.useSession();

  const { data: contextData, isLoading: contextLoading } = useQuery({
    queryKey: ["session", "context"],
    queryFn: () => apiFetch("/session/context"),
    enabled: !!session?.user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const value = useMemo(() => {
    if (!contextData) return null;

    const orgs = contextData.organizations || [];
    const activeOrgId = contextData.activeOrganizationId || null;
    const activeOrgRaw = contextData.activeOrganization || null;

    const activeOrganization = activeOrgRaw
      ? {
          ...activeOrgRaw,
          role: orgs.find((o) => o.id === activeOrgId)?.role || null,
          barbershops:
            orgs.find((o) => o.id === activeOrgId)?.barbershops || [],
        }
      : null;

    const planName =
      activeOrgRaw?.subscription?.plan?.name?.toLowerCase() || "";
    const planCode =
      activeOrgRaw?.subscription?.plan?.code?.toLowerCase() || "";
    const isPremium =
      planName.includes("premium") || planCode.includes("premium");
    const isPro = planName.includes("pro") || planCode.includes("pro");

    let maxStaff = (isPremium || isPro) ? Infinity : 1;
    const limitStaffObj = activeOrgRaw?.limits?.find(
      (l) => l.key === "max_staff",
    );
    if (limitStaffObj) {
      maxStaff = limitStaffObj.value;
    }
    const isSoloBarber = maxStaff <= 1;

    let deducedPlanCode = planCode || "base";
    let deducedPlanName = planName || "Plan Base";

    if (!planCode) {
      if (isPremium) {
        deducedPlanCode = "premium";
        deducedPlanName = "Plan Premium";
      } else if (maxStaff > 1 || isPro) {
        deducedPlanCode = "pro";
        deducedPlanName = "Plan Pro";
      }
    }

    return {
      activeOrganization,
      activeOrganizationId: activeOrgId,
      activeBarbershopId: contextData.activeBarbershopId || null,
      organizations: orgs,
      isSoloBarber,
      deducedPlanCode,
      deducedPlanName,

      subscription: contextData.activeOrganization?.subscription || null,
      isTrialing: contextData.activeOrganization?.isTrialing || false,
      features: contextData.activeOrganization?.features || [],
      limits: contextData.activeOrganization?.limits || [],

      hasFeature: (featureKey) => {
        const planName =
          contextData.activeOrganization?.subscription?.plan?.name?.toLowerCase() ||
          "";
        const planCode =
          contextData.activeOrganization?.subscription?.plan?.code?.toLowerCase() ||
          "";
        const isPro = planName.includes("pro") || planCode.includes("pro");
        const isPremium =
          planName.includes("premium") || planCode.includes("premium");

        const mappedFeatures = {
          commissions: isPro || isPremium,
          qr_intake: true,
          reports: isPro || isPremium,
          advanced_stats: isPremium,
          memberships: true,
          loyalty: true,
        };

        if (mappedFeatures[featureKey] !== undefined) {
          return mappedFeatures[featureKey];
        }

        return (
          contextData.activeOrganization?.features?.some(
            (f) => f.key === featureKey && f.isEnabled,
          ) ?? false
        );
      },
      getLimit: (limitKey) => {
        const planName =
          contextData.activeOrganization?.subscription?.plan?.name?.toLowerCase() ||
          "";
        const planCode =
          contextData.activeOrganization?.subscription?.plan?.code?.toLowerCase() ||
          "";
        const isPro = planName.includes("pro") || planCode.includes("pro");
        const isPremium =
          planName.includes("premium") || planCode.includes("premium");

        let fallbackLimit = Infinity;
        if (limitKey === "max_barbershops") {
          fallbackLimit = isPremium ? Infinity : 1;
        } else if (limitKey === "max_users" || limitKey === "max_staff") {
          fallbackLimit = (isPremium || isPro) ? Infinity : 1;
        } else if (limitKey === "max_customer_images") {
          fallbackLimit = 0;
        }

        const limitObj = contextData.activeOrganization?.limits?.find(
          (l) => l.key === limitKey,
        );
        return limitObj ? limitObj.value : fallbackLimit;
      },
    };
  }, [contextData]);

  const queryClient = useQueryClient();

  const handleRestart = async () => {
    try {
      if (authClient.signOut) {
        await authClient.signOut();
      }
    } catch (e) {
      console.error(e);
    }
    localStorage.clear();
    sessionStorage.clear();
    queryClient.clear();
    window.location.href = "/";
  };

  if (sessionPending || (session?.user && contextLoading)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Si el usuario no está logueado, no mostramos error, solo renderizamos hijos (ej: Login)
  if (!session?.user) {
    return (
      <OrganizationContext.Provider value={value}>
        {children}
      </OrganizationContext.Provider>
    );
  }

  // Si la petición falla, mostramos el error para saber qué pasó.
  if (contextData?.error || !contextData) {
    return (
      <Box sx={{ p: 4, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <Typography color="error" variant="h6">
          Error cargando el contexto de la organización
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
          }}
        >
          No se pudo cargar la información de tu organización. Por favor,
          intenta recargar la página. Si el problema persiste, contacta a
          soporte.
        </Typography>
        <Button variant="contained" color="primary" onClick={handleRestart}>
          Reiniciar Aplicación (Borrar caché)
        </Button>
      </Box>
    );
  }

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}
